import { type ReactNode } from "react";
import { Dialog } from "@base-ui/react/dialog";
import styles from "./ModalDialog.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import { IconX } from "@tabler/icons-react";
import IconButton from "./IconButton.tsx";
import Title from "./Title.tsx";
import Text from "./Text.tsx";
import useDialogPortalContainer from "../../hooks/use-dialog-portal-container.ts";

type Size = "xs" | "sm" | "md" | "lg" | "xl";

interface ModalDialogFooterProps {
  children?: ReactNode;
  start?: ReactNode;
  className?: string;
}

/**
 * Zone d'actions (Valider / Annuler / Précédent / Suivant) d'une modale.
 * À placer en dernier enfant du contenu (dans le <Form> s'il y en a un) :
 * elle reste collée en bas de la zone scrollable, donc toujours visible.
 *
 * Les `children` sont alignés à droite (action principale, ex: Suivant / Valider).
 * Le slot `start` est aligné à gauche, pour l'action secondaire de navigation
 * (ex: Précédent).
 */
export function ModalDialogFooter({
  children,
  start,
  className,
}: ModalDialogFooterProps) {
  return (
    <div className={classnames(styles.footer, className)} data-modal-footer="">
      {start && <div className={styles.footerStart}>{start}</div>}
      <div className={styles.footerEnd}>{children}</div>
    </div>
  );
}

/**
 * Zone de contenu scrollable d'une modale. À utiliser quand la modale a un
 * `ModalDialogFooter` qui doit rester un bloc fixe (comme le header) : seul ce
 * corps défile, le footer ne défile pas. Sans ce composant, le contenu (footer
 * compris) défile et le footer reste collé en bas (sticky).
 */
export function ModalDialogBody({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={classnames(styles.scrollBody, className)}>{children}</div>
  );
}

export interface ModalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  header?: ReactNode;
  tabsHeader?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  className?: string;
  noBackdropClose?: boolean;
  noPadding?: boolean;
  size?: Size;
  maxContentWidth?: Size;
}

export default function ModalDialog({
  open,
  onOpenChange,
  header,
  tabsHeader,
  subtitle,
  children,
  className,
  noBackdropClose,
  noPadding,
  size,
  maxContentWidth,
}: ModalDialogProps) {
  // Ouverte depuis une ancienne modale (<dialog> natif en top layer), la popup
  // est portée dans ce <dialog> : portée dans le body, elle resterait dessous.
  const { container, triggerRef } = useDialogPortalContainer("dialog");
  return (
    <Dialog.Root
      open={open}
      onOpenChange={onOpenChange}
      disablePointerDismissal={Boolean(noBackdropClose)}
    >
      <span hidden ref={triggerRef} />
      <Dialog.Portal container={container}>
        {/* forceRender: Base UI n'affiche pas le backdrop d'une modale imbriquée
            par défaut ; on le force pour dimmer la modale parente derrière. */}
        <Dialog.Backdrop forceRender className={styles.backdrop} />
        <Dialog.Viewport data-modal-viewport className={styles.viewport}>
          <Dialog.Popup
            className={classnames(
              className,
              styles.modal,
              size && styles[size],
              noPadding && styles.noPadding,
              maxContentWidth && styles[`mcw-${maxContentWidth}`],
            )}
          >
            {tabsHeader && <div className={styles.header}>{tabsHeader}</div>}
            {header && (
              <div className={styles.headerWrapper}>
                <Dialog.Title
                  className={styles.header}
                  render={
                    <Title weight={subtitle ? "bold" : undefined}>
                      {header}
                    </Title>
                  }
                />
                {subtitle && (
                  <Dialog.Description
                    className={styles.subtitle}
                    render={<Text color="muted">{subtitle}</Text>}
                  />
                )}
              </div>
            )}

            <div className={styles.body}>{children}</div>
            <Dialog.Close
              className={styles.close}
              render={<IconButton icon={IconX} label={"Fermer"} />}
            />
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
